/**
 * Pure presentation and selection helpers for the chat Activity_Indicator.
 *
 * This module is intentionally free of React and DOM so it can be unit- and
 * property-tested in isolation. It maps each known {@link ActivityType} to a
 * stable, distinct chip/trace label and an icon identifier, selects the live
 * (in-progress) activity for the streaming status line, and orders activities
 * for the collapsed summary and the expandable trace.
 *
 * Icons are represented as stable string keys (not JSX) so this module stays
 * pure; the Activity UI components resolve each key to a concrete SVG. Type is
 * always conveyed by text or a text alternative, never by color alone
 * (Requirement 13.7).
 */

import type { Activity, ActivityStatus } from '@/types/chat';

/**
 * Stable icon identifiers resolved to concrete SVGs by the Activity UI
 * components. `generic` is the fallback for reserved/unknown activity types.
 */
export type ActivityIconName = 'memory' | 'knowledge-base' | 'tool' | 'generic';

/** The text label and icon identifier used to present a single activity. */
export interface ActivityPresentation {
  /** Human-readable label (always non-empty). */
  label: string;
  /** Stable icon identifier (text-alternative friendly, never color alone). */
  icon: ActivityIconName;
}

/** Icon used for reserved/unknown activity types (Requirements 4.3, 10.3). */
const GENERIC_ICON: ActivityIconName = 'generic';

/** Fallback label when an activity carries no label of its own. */
const DEFAULT_LABEL = 'Activity';

/**
 * Dedicated presentation for the three release Activity_Types. Each maps to a
 * distinct, stable label applied consistently across all entries of that type
 * (Requirements 8.2, 9.3). Reserved types (`mcp_tool_call`, `agent_delegation`)
 * and any unrecognized type deliberately fall through to the generic
 * representation (Requirements 4.3, 10.3).
 */
const KNOWN_PRESENTATIONS: Record<
  'memory_retrieval' | 'knowledge_base_query' | 'tool_call',
  ActivityPresentation
> = {
  memory_retrieval: { label: 'Memory', icon: 'memory' },
  knowledge_base_query: { label: 'Knowledge base', icon: 'knowledge-base' },
  tool_call: { label: 'Tool call', icon: 'tool' },
};

/**
 * Resolve the presentation (label + icon) for an activity.
 *
 * Recognized release types get a stable, distinct label and a dedicated icon.
 * Reserved and unknown types get a generic representation derived from the
 * activity's own label, or a default descriptor when it carries no label
 * (Requirements 2.5, 4.3, 8.2, 10.3, 13.7).
 */
export function getActivityPresentation(activity: Activity): ActivityPresentation {
  const known =
    KNOWN_PRESENTATIONS[activity.type as keyof typeof KNOWN_PRESENTATIONS];
  if (known) {
    return known;
  }
  return { label: activity.label || DEFAULT_LABEL, icon: GENERIC_ICON };
}

/** A terminal status is one that ends an activity's lifecycle. */
function isTerminalStatus(status: ActivityStatus): boolean {
  return status === 'completed' || status === 'failed';
}

/**
 * Whether at least one activity is still in progress — `started` with no
 * terminal status yet. Used to decide whether to show the live status line.
 */
export function hasInProgressActivity(activities: Activity[]): boolean {
  return activities.some((activity) => !isTerminalStatus(activity.status));
}

/**
 * Select the single most-recently-started in-progress activity, or `undefined`
 * when every activity is terminal or the set is empty (Requirement 7.1).
 *
 * Activities are stored in the order their `started` events were received, so
 * the most-recently-started in-progress activity is the last non-terminal entry
 * in receipt order. At most one live status line is ever shown.
 */
export function selectLiveActivity(activities: Activity[]): Activity | undefined {
  for (let i = activities.length - 1; i >= 0; i -= 1) {
    const activity = activities[i];
    if (activity && !isTerminalStatus(activity.status)) {
      return activity;
    }
  }
  return undefined;
}

/**
 * Order activities for the collapsed summary and the expanded trace: ascending
 * occurrence timestamp, with ties broken by receipt order (Requirements 8.1,
 * 9.3).
 *
 * The result contains exactly one entry per input activity (length-preserving).
 * The input array is treated as receipt order, and the original index is used as
 * a stable tie-breaker so equal timestamps keep their received order regardless
 * of the engine's sort stability.
 */
export function orderActivities(activities: Activity[]): Activity[] {
  return activities
    .map((activity, index) => ({ activity, index }))
    .sort((a, b) => {
      const ta = Date.parse(a.activity.ts);
      const tb = Date.parse(b.activity.ts);
      const aValid = !Number.isNaN(ta);
      const bValid = !Number.isNaN(tb);

      // Both parse to real times: order by time, ties by receipt order.
      if (aValid && bValid) {
        return ta === tb ? a.index - b.index : ta - tb;
      }
      // Fall back to a deterministic string compare when a timestamp is not a
      // parseable date, so ordering stays total and stable.
      if (!aValid && !bValid) {
        if (a.activity.ts === b.activity.ts) {
          return a.index - b.index;
        }
        return a.activity.ts < b.activity.ts ? -1 : 1;
      }
      // Parseable timestamps sort ahead of unparseable ones.
      return aValid ? -1 : 1;
    })
    .map((entry) => entry.activity);
}
